// Pragmatic JSON-Schema → field-descriptor parser.
//
// The runner accepts ANY JSON Schema for a workflow's input contract, but
// the builder UI only renders a flat field list (DEV-16143) and a flat
// run-trigger form (DEV-16142). This helper boils down the schema to that
// shape: one descriptor per top-level property of an object schema, with
// `type` / `required` / `description` resolved for display.
//
// Nested objects / arrays of objects are surfaced with a flat `"object"`
// or `"array"` type label and rendered as raw JSON in the form — drilling
// in deeper is out of scope for this iteration; the JSON textarea editor
// remains the escape hatch for advanced schemas.

/** What kind of input control / display row matches this field. */
export type SchemaFieldType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'unknown';

export interface ISchemaField {
  /** Property name as it appears under `properties[…]`. */
  name: string;
  /** Resolved type — best-effort from `type` / `enum` / `anyOf`. */
  type: SchemaFieldType;
  /** True when the parent's `required` array contains this name. */
  required: boolean;
  /** Free-form description from the property's `description`, if any. */
  description?: string;
  /** Pre-supplied default — useful for the form, ignored by the view. */
  defaultValue?: unknown;
  /** Closed enum of allowed values — useful for the form to render a select. */
  enumValues?: readonly unknown[];
}

/**
 * Parse a JSON Schema object into a flat list of top-level property
 * descriptors. Returns `null` when the schema isn't an `{ type: "object" }`
 * schema with `properties` — caller treats that as "no contract / accepts
 * free-form input".
 */
export function parseInputSchemaFields(schema: unknown): ISchemaField[] | null {
  if (!isPlainObject(schema)) return null;
  if (schema.type !== 'object') return null;
  const properties = schema.properties;
  if (!isPlainObject(properties)) return null;

  const required = Array.isArray(schema.required)
    ? new Set(schema.required.filter((v): v is string => typeof v === 'string'))
    : new Set<string>();

  const fields: ISchemaField[] = [];
  for (const [name, raw] of Object.entries(properties)) {
    if (!isPlainObject(raw)) continue;
    fields.push({
      name,
      type: resolveType(raw),
      required: required.has(name),
      description: typeof raw.description === 'string' ? raw.description : undefined,
      defaultValue: 'default' in raw ? raw.default : undefined,
      enumValues: Array.isArray(raw.enum) ? (raw.enum as readonly unknown[]) : undefined,
    });
  }
  return fields;
}

function resolveType(prop: Record<string, unknown>): SchemaFieldType {
  // `type` can be a string OR an array (`["string", "null"]`). Pick the
  // first non-`null` for display purposes.
  const t = prop.type;
  if (typeof t === 'string') return normalizeType(t);
  if (Array.isArray(t)) {
    const first = t.find((v): v is string => typeof v === 'string' && v !== 'null');
    if (first) return normalizeType(first);
  }
  // `anyOf` / `oneOf` — pick the first member's `type` if it's plain.
  for (const key of ['anyOf', 'oneOf'] as const) {
    const variants = prop[key];
    if (Array.isArray(variants)) {
      for (const v of variants) {
        if (isPlainObject(v) && typeof v.type === 'string' && v.type !== 'null') {
          return normalizeType(v.type);
        }
      }
    }
  }
  if (Array.isArray(prop.enum)) return 'string';
  return 'unknown';
}

function normalizeType(raw: string): SchemaFieldType {
  switch (raw) {
    case 'string':
    case 'number':
    case 'integer':
    case 'boolean':
    case 'object':
    case 'array':
      return raw;
    default:
      return 'unknown';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
